<template>
  <div
    :key="itemType"
    :class="$style.groupsMainPanel"
  >
    <BannerController
      :bannerContent="bannerContent"
    />

    <GroupsGetStartedInstructions
      v-if="showGettingStartedCollapsableSection"
    />

    <RecommendedPolicyGroups
      v-if="shouldShowRecommendedGroups"
      @loadDefaultGroupsBtn="handleLoadingOverlay"
    />

    <AnnouncementWrapper
      v-if="showAnnouncement"
      :caretOffset="announcement.caretOffset"
      :caretPointsTo="announcement.caretPointsTo"
      :class="$style.announcement"
      :header="announcement.header"
      :localStorageKey="announcement.localStorageKey"
      :mainActionText="announcement.mainActionText"
      :text="announcement.text"
      @main-action="handleOpenNewPolicyGroupTemplate"
    />

    <GroupItemTable
      ref="itemTable"
      :asidePanelCanceledEventName="legacyAsidePanelCanceledEventName"
      :handleColumnAction="handleColumnAction"
      :isAsideOpen="isAsideOpen"
      :itemType="itemType"
      :selectedModelId="selectedModelId"
      :selectionItemText="{
        label: 'selected',
        plural: 'groups',
        singular: 'group',
      }"
      :setGettingStartedTooltipVisibility="showTooltip"
      :setSelectedModelId="setSelectedModelId"
      :viewport="viewport"
      @policyGroupsDeleted="showRecommendedPolicyGroups"
    />

    <MembershipSuggestionsModal
      v-if="enableMembershipSuggestionsModal"
      :handleCancel="membershipSuggestionsModalData.handleCancel"
      :handleContinue="membershipSuggestionsModalHandleContinue"
      :itemId="membershipSuggestionsModalData.itemId"
      :itemName="membershipSuggestionsModalData.itemName"
      itemType="suggestedSystemUser"
      :review="true"
      @close="() => toggleMembershipSuggestionsModal(false)"
    />

    <LoadingOverlay
      :showLoadingOverlay="setLoadingOverlay"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import AnnouncementWrapper from 'client/components/Announcement/AnnouncementWrapper.vue';
import BannerConstants from 'client/util/Constants/BannerConstants';
import BannerController from 'client/components/ViewportBanner/BannerController.vue';
import BannerType from 'client/components/ViewportBanner/BannerType';
import Constants, {
  FilterComponentConsts,
  ItemTypeConsts,
  ItemTypeDisplayNames,
  LocalStorageConsts,
  StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import DateUtil from 'client/util/DateUtil';
import GroupItemTable from 'components/ItemTable/GroupItemTable.vue';
import GroupsGetStartedInstructions from 'client/groups/GroupsGetStartedInstructions.vue';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay.vue';
import MainPanelMixin from 'components/MainPanel/mixins/MainPanelMixin';
import MainPanelWithItemTableMixin from 'components/MainPanel/mixins/MainPanelWithItemTableMixin';
import MembershipSuggestionsModal from 'client/groups/User/MembershipSuggestionsModal.vue';
import RecommendedPolicyGroups from 'client/groups/RecommendedPolicyGroups';
import RecommendedPolicyGroupsData from 'client/util/Constants/RecommendedPolicyGroupsConstants';
import Util from 'client/util/Util';
import pluralize from 'pluralize';

const { groupsInstructionsDismissed } = LocalStorageConsts;
const groupCollection = StoreModuleMap[ItemTypeConsts.configurationGroup];

export default {
  name: 'GroupsMainPanel',

  components: {
    AnnouncementWrapper,
    BannerController,
    GroupItemTable,
    GroupsGetStartedInstructions,
    LoadingOverlay,
    MembershipSuggestionsModal,
    RecommendedPolicyGroups,
  },

  mixins: [MainPanelMixin, MainPanelWithItemTableMixin],

  props: {
    itemType: {
      type: String,
      default: ItemTypeConsts.group,
    },
  },

  data: () => ({
    collapsed: false,
    FilterComponentConsts,
    infoStartDate: '2020-04-28',
    localStorageKey: groupsInstructionsDismissed,
    membershipSuggestionsModalData: {
      itemId: null,
      itemName: null,
    },
    enableMembershipSuggestionsModal: false,
    setLoadingOverlay: false,
    shouldShowRecommendedGroups: false,
    announcement: {
      header: 'Create Policy Groups from Templates',
      localStorageKey: LocalStorageConsts.policyGroupTemplateAnnouncementSeen,
      text: 'Policy group templates let you apply pre-defined groups of security and compliance policies to organizations for quick and efficient reuse.',
      mainActionText: 'Create Policy Group',
      caretOffset: 20,
      caretPointsTo: 'left',
    },
  }),

  computed: {
    ...mapGetters('OrganizationsCollection', {
      orgCreatedDate: 'getOrgCreatedDate',
    }),

    ...mapGetters(groupCollection, [
      'getCollection',
    ]),

    ...mapGetters('SettingsModule', [
      'isAdminWithBilling',
      'isAdmin',
      'isManager',
    ]),

    ...mapState('SettingsModule', [
      'orgId',
      'user',
    ]),

    bannerContent() {
      return new BannerType({
        bannerVisibility: () => this.isUserGroupPanel
          && this.$hasFeatureFlag(this.$featureFlags.membershipSuggestionAutomation),
        name: BannerConstants.userGroupAutomatedMembers.name,
        bannerText: BannerConstants.userGroupAutomatedMembers.text,
        canCloseBanner: true,
        localStorageKey: LocalStorageConsts.bannerDismissedUserGroupAutomatedMembers,
        learnMoreLink: BannerConstants.userGroupAutomatedMembers.learnMoreLink,
        learnMoreText: BannerConstants.userGroupAutomatedMembers.learnMoreText,
        startDate: '2022/11/1',
        endDate: '2022/12/31',
      });
    },

    isUserGroupPanel() {
      return this.itemType === ItemTypeConsts.userGroup;
    },

    showAnnouncement() {
      return this.itemType === ItemTypeConsts.configurationGroup
      && (this.isAdminWithBilling || this.isAdmin || this.isManager);
    },

    showGettingStartedCollapsableSection() {
      return DateUtil.isAfterDate(this.orgCreatedDate, this.infoStartDate)
        && this.itemType !== ItemTypeConsts.configurationGroup;
    },

  },

  created() {
    // TODO: The actual string is used instead of GroupDetailsAsidePanel.FULL_CANCELED_EVENT_NAME
    // because importing GroupDetailsAsidePanel causes test failures in GroupDetailsAsidePanelTest
    this.legacyAsidePanelCanceledEventName = 'group-details:canceled';
    this.legacyAsidePanelSavedEventName = 'group-details:saved';
    this.showRecommendedPolicyGroups();
  },

  methods: {
    ...mapActions(StoreModelMap[ItemTypeConsts.configurationGroup], [
      'fetchPolicyGroupsBySearchTerm',
    ]),

    handleColumnAction(id, name) {
      this.membershipSuggestionsModalData.itemId = id;
      this.membershipSuggestionsModalData.itemName = name;

      this.toggleMembershipSuggestionsModal(true);
    },

    handleLoadingOverlay(val) {
      this.shouldShowRecommendedGroups = !!val;
      this.setLoadingOverlay = val;
    },

    handleOpenNewPolicyGroupTemplate() {
      // using Util.redirect becuase of a $router.push bug
      Util.redirect('/#/groups/configuration/grouptemplates');
    },

    async membershipSuggestionsModalHandleContinue(params = {}) {
      try {
        notify(Constants.success, `${ItemTypeDisplayNames[this.itemType]} saved successfully!`);

        if (params) {
          const usersAddedMsg = `${params.usersAdded} ${pluralize('user', params.usersAdded, false)} ${pluralize('was', params.usersAdded, false)} added`;
          const usersRemovedMsg = `${params.usersRemoved} ${pluralize('user', params.usersRemoved, false)} ${pluralize('was', params.usersRemoved, false)} removed`;
          notify(Constants.success, `${usersAddedMsg} and ${usersRemovedMsg}.`);
        }
      } catch (e) {
        notify(Constants.error, `Unable to save user group! ${e.toString()}`);
      }
    },

    async showRecommendedPolicyGroups() {
      const defaultGroupPolicies = await this.fetchPolicyGroupsBySearchTerm('ring');
      let areDefaultGroups = false;

      if (defaultGroupPolicies.length > 0) {
        areDefaultGroups = defaultGroupPolicies.some(
          (policyGroup) => RecommendedPolicyGroupsData.some(
            (recommendedPolicyGroup) => recommendedPolicyGroup.name === policyGroup.name,
          ),
        );
      }

      this.shouldShowRecommendedGroups = this.itemType === ItemTypeConsts.configurationGroup
        && !areDefaultGroups;
    },

    toggleMembershipSuggestionsModal(show) {
      this.enableMembershipSuggestionsModal = show;
    },
  },
};
</script>

<style module scoped>
.announcement {
  left: 90px;
  width: 400px;
}

.groupsMainPanel {
  height: 100%;
}
</style>
